import { Controller } from "@hotwired/stimulus"

// PCO detail: sync-in-place autosaving lines grid (add/remove rows), header
// autosave (title/markup/days/reason), lifecycle actions, attachment chips.
export default class extends Controller {
  static targets = ["linesBody", "linesTotal", "markupAmount", "grandTotal", "saveStatus",
                    "title", "markupInput", "daysInput", "reason",
                    "rejectModal", "rejectReason", "deleteModal", "voidModal", "reopenModal", "amendModal",
                    "finishAmendModal", "finishAmendReason",
                    "attachmentList", "chainPanel"]
  static values = {
    editable: Boolean, canManage: Boolean,
    updateUrl: String, updateLinesUrl: String, submitUrl: String, approveUrl: String,
    rejectUrl: String, voidUrl: String, reopenUrl: String, amendUrl: String, finishAmendUrl: String, destroyUrl: String,
    presignUrl: String, registerUrl: String, attachmentsUrl: String
  }

  connect() {
    this._loadAttachments()
    // Fresh PCO: put the cursor in the first line's Description so the user
    // can start typing right away (skipped once it has content).
    if (this.editableValue && this.hasLinesBodyTarget) {
      const first = this.linesBodyTarget.querySelector('input[data-field="description"]')
      if (first && !first.value) first.focus()
    }
    const msg = sessionStorage.getItem("builder-finance.flash")
    if (msg) {
      sessionStorage.removeItem("builder-finance.flash")
      this._showToast(msg)
    }
  }

  disconnect() { clearTimeout(this._saveTimer) }

  // ── Lines ──
  lineInput(e) {
    const row = e.target.closest("tr")
    const qty = this._num(row.querySelector('[data-field="quantity"]')?.value)
    const price = this._num(row.querySelector('[data-field="unit_price"]')?.value)
    const total = Math.round(((qty || 1) * price) * 100) / 100
    row.querySelector('[data-cell="total"]').textContent = "$" + this._fmt(total)
    this._recalcTotals()
    this._queueSave()
  }

  addLine() {
    const tr = document.createElement("tr")
    tr.innerHTML = `
      <td class="px-1 py-1"><input type="text" data-field="item" data-action="input->gc-pco-detail#lineInput" class="w-full border border-gray-200 rounded px-1.5 py-0.5 text-[13px]"></td>
      <td class="px-1 py-1"><input type="text" data-field="description" data-action="input->gc-pco-detail#lineInput" class="w-full border border-gray-200 rounded px-1.5 py-0.5 text-[13px]"></td>
      <td class="px-1 py-1"><input type="text" data-field="quantity" data-action="input->gc-pco-detail#lineInput" class="w-full border border-gray-200 rounded px-1.5 py-0.5 text-right text-[13px]"></td>
      <td class="px-1 py-1"><input type="text" data-field="unit" data-action="input->gc-pco-detail#lineInput" class="w-full border border-gray-200 rounded px-1.5 py-0.5 text-[13px]"></td>
      <td class="px-1 py-1"><div class="flex items-center gap-1"><span class="text-gray-400 text-[13px] shrink-0">$</span><input type="text" data-field="unit_price" data-action="input->gc-pco-detail#lineInput" class="w-full border border-gray-200 rounded px-1.5 py-0.5 text-right text-[13px]"></div></td>
      <td class="px-2 py-1.5 text-right tabular-nums font-medium" data-cell="total">$0.00</td>
      <td class="px-1 py-1 text-right"><button data-action="click->gc-pco-detail#removeLine" class="text-gray-300 hover:text-red-500 cursor-pointer" tabindex="-1">✕</button></td>`
    this.linesBodyTarget.appendChild(tr)
    // Pre-fill the next sequential item number (new lines auto-number);
    // still editable.
    tr.querySelector('[data-field="item"]').value = String(this._nextItemNumber())
    tr.querySelector('[data-field="description"]').focus()
  }

  // Highest numeric item # in the grid + 1 (blank / non-numeric items ignored).
  _nextItemNumber() {
    let max = 0
    this.linesBodyTarget.querySelectorAll('[data-field="item"]').forEach((el) => {
      const n = parseInt(String(el.value).trim(), 10)
      if (!isNaN(n) && n > max) max = n
    })
    return max + 1
  }

  removeLine(e) {
    e.currentTarget.closest("tr").remove()
    this._renumberLines()
    this._recalcTotals()
    this._queueSave()
  }

  // After a middle row is deleted, close the gap: rows with numeric item
  // numbers get resequenced 1..N in display order. Custom (non-numeric)
  // items are left alone.
  _renumberLines() {
    let n = 0
    this.linesBodyTarget.querySelectorAll('[data-field="item"]').forEach((el) => {
      const current = String(el.value).trim()
      if (current === "" || !isNaN(parseInt(current, 10))) {
        n += 1
        el.value = String(n)
      }
    })
  }

  _recalcTotals() {
    let total = 0
    this.linesBodyTarget.querySelectorAll("tr").forEach((row) => {
      const qty = this._num(row.querySelector('[data-field="quantity"]')?.value)
      const price = this._num(row.querySelector('[data-field="unit_price"]')?.value)
      total += (qty || 1) * price
    })
    const markupPct = this.hasMarkupInputTarget ? this._num(this.markupInputTarget.value) : 0
    const markup = total * markupPct / 100
    this.linesTotalTarget.textContent = "$" + this._fmt(total)
    this.markupAmountTarget.textContent = "$" + this._fmt(markup)
    this.grandTotalTarget.textContent = "$" + this._fmt(total + markup)
  }

  _queueSave() {
    if (!this.editableValue) return
    if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = "Saving…"
    clearTimeout(this._saveTimer)
    this._saveTimer = setTimeout(() => this._saveLines(), 1000)
  }

  async _saveLines() {
    const lines = Array.from(this.linesBodyTarget.querySelectorAll("tr")).map((row) => ({
      id: row.dataset.lineId || null,
      item: row.querySelector('[data-field="item"]')?.value,
      description: row.querySelector('[data-field="description"]')?.value,
      quantity: row.querySelector('[data-field="quantity"]')?.value,
      unit: row.querySelector('[data-field="unit"]')?.value,
      unit_price: row.querySelector('[data-field="unit_price"]')?.value
    }))
    try {
      const resp = await fetch(this.updateLinesUrlValue, {
        method: "PATCH", headers: this._headers(), body: JSON.stringify({ lines })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Save failed")
      const rows = this.linesBodyTarget.querySelectorAll("tr")
      ;(data.line_ids || []).forEach((id, i) => { if (rows[i]) rows[i].dataset.lineId = id })
      this._applyTotals(data)
      if (this.hasSaveStatusTarget) {
        this.saveStatusTarget.textContent = "Saved ✓"
        setTimeout(() => { this.saveStatusTarget.textContent = "Changes save automatically" }, 2000)
      }
    } catch (err) {
      if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = "Save failed — edit again to retry"
      this._showToast(err.message, "alert")
    }
  }

  async saveHeader() {
    const body = {}
    if (this.hasTitleTarget) body.title = this.titleTarget.value
    if (this.hasMarkupInputTarget) body.markup_pct = this.markupInputTarget.value
    if (this.hasDaysInputTarget) body.time_extension_days = this.daysInputTarget.value
    if (this.hasReasonTarget) body.reason = this.reasonTarget.value
    const resp = await fetch(this.updateUrlValue, { method: "PATCH", headers: this._headers(), body: JSON.stringify(body) })
    const data = await resp.json()
    if (resp.ok) this._applyTotals(data)
    else this._showToast(data.error || "Could not save", "alert")
  }

  _applyTotals(t) {
    if (t.lines_total === undefined) return
    this.linesTotalTarget.textContent = "$" + this._fmt(t.lines_total)
    this.markupAmountTarget.textContent = "$" + this._fmt(t.markup_amount)
    this.grandTotalTarget.textContent = "$" + this._fmt(t.total_with_markup)
  }

  // ── Lifecycle ──
  async submit() { await this._post(this.submitUrlValue, {}, "Submitted — the owner has it now") }
  async approve() { await this._post(this.approveUrlValue, {}, "Acknowledged — bundle it into a Change Order to move the contract sum") }
  async void() { await this._post(this.voidUrlValue, {}, "Voided") }
  async reopen() { await this._post(this.reopenUrlValue, {}, "Withdrawn — back to draft, make your changes and send it again") }
  async amend() { await this._post(this.amendUrlValue, {}, "Amended — make your changes, then click Finish amend") }
  // Finish amend runs through its own modal so the user can leave an optional
  // reason (Larry 2026-07-21 item 2) — it lands on the History Log.
  async finishAmend() {
    const reason = this.hasFinishAmendReasonTarget ? this.finishAmendReasonTarget.value.trim() : ""
    await this._post(this.finishAmendUrlValue, { reason }, "Amend finished — acknowledged again, nobody was notified")
  }

  async reject() {
    await this._post(this.rejectUrlValue, { reason: this.rejectReasonTarget.value }, "Rejected — sent back to the GC")
  }

  async destroy() {
    const resp = await fetch(this.destroyUrlValue, { method: "DELETE", headers: this._headers() })
    if (resp.ok) {
      sessionStorage.setItem("builder-finance.flash", "PCO deleted")
      window.location.href = window.location.pathname.replace(/\/[^/]+$/, "")
    } else {
      const data = await resp.json().catch(() => ({}))
      this.closeModals()
      this._showToast(data.error || "Could not delete this PCO", "alert")
    }
  }

  async _post(url, body, message) {
    try {
      const resp = await fetch(url, { method: "POST", headers: this._headers(), body: JSON.stringify(body) })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Action failed")
      sessionStorage.setItem("builder-finance.flash", message)
      window.location.reload()
    } catch (err) {
      this.closeModals()
      this._showToast(err.message, "alert")
    }
  }

  // ── Modals ──
  openRejectModal() { this.rejectModalTarget.classList.remove("hidden") }
  openDeleteModal() { this.deleteModalTarget.classList.remove("hidden") }
  openVoidModal() { this.voidModalTarget.classList.remove("hidden") }
  openReopenModal() { this.reopenModalTarget.classList.remove("hidden") }
  openAmendModal() { this.amendModalTarget.classList.remove("hidden") }
  openFinishAmendModal() {
    this.finishAmendModalTarget.classList.remove("hidden")
    if (this.hasFinishAmendReasonTarget) this.finishAmendReasonTarget.focus()
  }
  closeModals() {
    if (this.hasRejectModalTarget) this.rejectModalTarget.classList.add("hidden")
    if (this.hasDeleteModalTarget) this.deleteModalTarget.classList.add("hidden")
    if (this.hasVoidModalTarget) this.voidModalTarget.classList.add("hidden")
    if (this.hasReopenModalTarget) this.reopenModalTarget.classList.add("hidden")
    if (this.hasAmendModalTarget) this.amendModalTarget.classList.add("hidden")
    if (this.hasFinishAmendModalTarget) this.finishAmendModalTarget.classList.add("hidden")
  }
  stopPropagation(e) { e.stopPropagation() }

  // ── Attachments ──
  async _loadAttachments() {
    if (!this.hasAttachmentListTarget) return
    try {
      const resp = await fetch(this.attachmentsUrlValue, { headers: { "Accept": "application/json" } })
      const files = await resp.json()
      this.attachmentListTarget.innerHTML = files.length ? "" : '<span class="text-[13px] text-gray-400">No files attached.</span>'
      files.forEach((f) => this.attachmentListTarget.insertAdjacentHTML("beforeend", this._chipHtml(f)))
    } catch {
      this.attachmentListTarget.innerHTML = '<span class="text-[13px] text-gray-400">Could not load files.</span>'
    }
  }

  _chipHtml(f) {
    const esc = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    return `<span class="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 rounded-lg px-2.5 py-1 text-[13px]" data-attachment-id="${f.id}">
      <a href="${f.url}" target="_blank" class="hover:text-brand max-w-[180px] truncate">${esc(f.filename)}</a>
      ${this.canManageValue ? `<button data-action="click->gc-pco-detail#removeAttachment" data-id="${f.id}" class="text-gray-400 hover:text-red-500 cursor-pointer">✕</button>` : ""}
    </span>`
  }

  async filesChosen(e) {
    const files = Array.from(e.target.files || [])
    e.target.value = ""
    await this._uploadFileList(files)
  }

  // ── Drag-and-drop onto the supporting-docs card (inline highlight, no
  //    full-page overlay — per the drop-zone convention). ──
  dragOver(e) {
    e.preventDefault()
    e.currentTarget.classList.add("ring-2", "ring-brand", "bg-brand/5")
  }
  dragLeave(e) {
    e.currentTarget.classList.remove("ring-2", "ring-brand", "bg-brand/5")
  }
  async dropFiles(e) {
    e.preventDefault()
    e.currentTarget.classList.remove("ring-2", "ring-brand", "bg-brand/5")
    if (!this.canManageValue) return
    await this._uploadFileList(Array.from(e.dataTransfer?.files || []))
  }

  async _uploadFileList(files) {
    for (const file of files) {
      try {
        const presign = await fetch(this.presignUrlValue, {
          method: "POST", headers: this._headers(),
          body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
        })
        const pd = await presign.json()
        if (!presign.ok) throw new Error(pd.error || "Upload refused")
        const u = pd.uploads[0]
        await fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: file })
        await fetch(this.registerUrlValue, {
          method: "POST", headers: this._headers(),
          body: JSON.stringify({ files: [{ cache_name: u.cache_name, filename: u.filename }] })
        })
      } catch (err) {
        this._showToast(`${file.name}: ${err.message}`, "alert")
      }
    }
    this._loadAttachments()
  }

  async removeAttachment(e) {
    // Grab the chip BEFORE awaiting — e.currentTarget is null once the event
    // finishes dispatching, so reading it after the fetch silently fails.
    const chip = e.currentTarget.closest("[data-attachment-id]")
    const id = e.currentTarget.dataset.id
    const resp = await fetch(`${this.attachmentsUrlValue}/${id}`, { method: "DELETE", headers: this._headers() })
    if (resp.ok) {
      chip?.remove()
      if (this.hasAttachmentListTarget && !this.attachmentListTarget.querySelector("[data-attachment-id]")) {
        this.attachmentListTarget.innerHTML = '<span class="text-[13px] text-gray-400">No files attached.</span>'
      }
    } else {
      this._showToast("Could not remove the file — try again.", "alert")
    }
  }

  _num(v) {
    const n = parseFloat(String(v || "").replace(/[^0-9.\-]/g, ""))
    return isNaN(n) ? 0 : n
  }
  _fmt(n) { return (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }

  _headers() {
    return {
      "Content-Type": "application/json", "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  _showToast(message, type = "notice") {
    const div = document.createElement("div")
    div.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    div.setAttribute("data-controller", "flash")
    div.setAttribute("data-flash-dismiss-after-value", "5000")
    div.textContent = message
    document.body.prepend(div)
  }
}
